import React, { useState, useCallback, useEffect } from 'react';
import moment from 'moment';
import { groupBy, isEmpty } from 'ramda';
import { useQuery } from '@apollo/react-hooks';
import { GET_JOBS_VISITS } from '../apollo/queries';
import { Page, Card, AppProvider, Frame, Select, Stack, DataTable, EmptyState } from '@shopify/polaris';
import { TitleBar } from '@shopify/app-bridge-react';
import Loader from '../components/SkeletonLoader';
import { Bar } from 'react-chartjs-2';
import randomColor from 'randomcolor';

const dateOptions = [
  { label: 'All', value: 'all' },
  { label: 'Today', value: '1' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 14 days', value: '14' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Last year', value: '365' },
];

const JobDetailsViewsReport = ({ history, shopifyToken }) => {
  const { data: jobsData = {}, loading } = useQuery(GET_JOBS_VISITS, {
    variables: { shopifyToken, templates: false },
  });

  const { jobs: jobsWrapper = {} } = { ...jobsData };
  const { jobs = [] } = { ...jobsWrapper };

  const visits = jobs
    .map(({ visits = [], id: jobId, title: jobTitle }) => {
      return visits.map((v) => ({ ...v, jobId, jobTitle }));
    })
    .reduce((a, b) => [...a, ...b], []);

  let jobOptions = jobs.map((el) => {
    const { id: value, title: label } = { ...el };
    return { label, value };
  });
  jobOptions = [{ label: 'All jobs', value: '' }, ...jobOptions];

  const [selectedJob, setSelectedJob] = useState('');
  const [visitsTable, setVisitsTable] = useState([]);
  const [visitsTotals, setVisitsTotals] = useState([]);
  const [overTimeData, setOverTimeData] = useState();

  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [dateRange, setDateRange] = useState('all');
  const handleDateRangeChange = useCallback((newValue) => {
    setDateRange(newValue);
    if (newValue === 'all') {
      setStartDate();
    } else {
      setStartDate(moment(new Date()).subtract(Number(newValue), 'days'));
    }
  }, []);

  const filterVisits = () => {
    let selectedVisits = !!selectedJob
      ? visits.filter((el) => {
          const { jobId = '' } = { ...el };
          return jobId === selectedJob;
        })
      : visits;

    selectedVisits = selectedVisits.filter(({ startedAt }) => {
      return moment(startedAt).isBetween(moment(startDate || '01/01/1970'), moment(endDate || new Date()));
    });

    return selectedVisits;
  };

  const buildOverTimeData = () => {
    const selectedVisits = filterVisits();

    let selectedDates = Array.from(
      new Set(selectedVisits.map(({ startedAt }) => moment(startedAt).format('MMMM DD, YYYY')))
    ).sort((a, b) => moment(a).format('YYYYMMDD') - moment(b).format('YYYYMMDD'));

    const selectedDevices = Array.from(
      new Set(
        selectedVisits.map(({ deviceType = 'Other' }) => {
          return !!deviceType ? deviceType : 'Other';
        })
      )
    );

    const rgbColors = selectedDevices.map(() => randomColor({ format: 'rgbArray' }));

    const datasets = selectedDevices.map((el, ind) => {
      const borderColor = `rgba(${rgbColors[ind][0]}, ${rgbColors[ind][1]}, ${rgbColors[ind][2]}, 0.2)`;
      const backgroundColor = `rgba(${rgbColors[ind][0]}, ${rgbColors[ind][1]}, ${rgbColors[ind][2]}, 0.6)`;
      return {
        data: selectedDates.map((date) => {
          return selectedVisits.filter(({ startedAt, deviceType = 'Other' }) => {
            return moment(startedAt).format('YYYYMMDD') === moment(date).format('YYYYMMDD') && (deviceType || 'Other') === el;
          }).length;
        }),
        label: el || 'Other',
        backgroundColor,
        borderColor,
        borderWidth: 1,
      };
    });

    setOverTimeData({
      labels: selectedDates,
      datasets,
      isEmpty: isEmpty(selectedVisits),
    });
  };

  useEffect(() => {
    buildOverTimeData();
    buildVisitsTable();
  }, [jobs, selectedJob, startDate, endDate]);

  const buildVisitsTable = () => {
    const selectedVisits = filterVisits();
    const groupedVisits = groupBy(({ startedAt }) => moment(startedAt).format('MMM DD, YYYY'), selectedVisits);

    const rows = Object.keys(groupedVisits)
      .sort((a, b) => moment(a).format('YYYYMMDD') - moment(b).format('YYYYMMDD'))
      .map((deviceKey) => {
        const devicedVisits = groupBy(({ deviceType }) => deviceType || 'Other', groupedVisits[deviceKey]);
        return [
          deviceKey,
          (devicedVisits['Desktop'] || []).length,
          (devicedVisits['Mobile'] || []).length,
          (devicedVisits['Tablet'] || []).length,
          (devicedVisits['Other'] || []).length,
          (devicedVisits['Desktop'] || []).length +
            (devicedVisits['Mobile'] || []).length +
            (devicedVisits['Tablet'] || []).length +
            (devicedVisits['Other'] || []).length,
        ];
      });

    const totalVisitsGroups = groupBy(({ deviceType }) => deviceType || 'Other', selectedVisits);
    setVisitsTable(rows);
    setVisitsTotals([
      '',
      (totalVisitsGroups['Desktop'] || []).length,
      (totalVisitsGroups['Mobile'] || []).length,
      (totalVisitsGroups['Tablet'] || []).length,
      (totalVisitsGroups['Other'] || []).length,
      (totalVisitsGroups['Desktop'] || []).length +
        (totalVisitsGroups['Mobile'] || []).length +
        (totalVisitsGroups['Tablet'] || []).length +
        (totalVisitsGroups['Other'] || []).length,
    ]);
  };

  if (!loading) {
    return (
      <AppProvider>
        <Frame>
          <TitleBar title='Job description page views' />
          <Page breadcrumbs={[{ content: 'Analytics', onAction: () => { history.push('/analytics'); } }]} title='Job description page views'>
            <Stack vertical>
              <Stack distribution='fill'>
                <Select label={'Date range'} options={dateOptions} onChange={handleDateRangeChange} value={dateRange} />
                <Select label='Job title' options={jobOptions} onChange={setSelectedJob} value={selectedJob} />
                <Stack.Item></Stack.Item>
                <Stack.Item></Stack.Item>
              </Stack>
              <Card>
                <Card.Section title={'Job description page views over time'}>
                  {(overTimeData || {}).isEmpty ? (
                    <EmptyState heading='No data found'>
                      <p>{`There were no page views in this date range.`}</p>
                    </EmptyState>
                  ) : (
                    <Bar
                      data={overTimeData}
                      options={{
                        scales: {
                          yAxes: [
                            {
                              ticks: {
                                beginAtZero: true,
                              },
                              stacked: true,
                            },
                          ],
                          xAxes: [
                            {
                              stacked: true,
                              type: 'time',
                              distribution: 'series',
                              time: {
                                unit: 'day',
                              },
                            },
                          ],
                        },
                      }}
                    />
                  )}
                </Card.Section>
              </Card>
              <span className={'charts-datatable'}>
                <Card>
                  <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
                    headings={['Day', 'Desktop', 'Mobile', 'Tablet', 'Other', 'Total']}
                    rows={visitsTable}
                    totals={visitsTotals}
                    totalsName={{ plural: 'Summary', singular: 'Summary' }}
                    footerContent={`Showing ${visitsTotals[5]} of ${
                      visits.filter(({ landingPage }) => {
                        return landingPage.indexOf('/jobs/') !== -1;
                      }).length
                    } results`}
                  />
                </Card>
              </span>
            </Stack>
            <br />
          </Page>
        </Frame>
      </AppProvider>
    );
  } else {
    return (
      <AppProvider>
        <Loader />
      </AppProvider>
    );
  }
};

export default JobDetailsViewsReport;
